/**
 * Language and theme preferences.
 */
import React, { useEffect, useState } from "react";
import { Box, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import { VText, VSwitch, VPassword, VButton, VFormItem } from "form";
import { useAsterController } from "context";
import { actionDarkMode, actionEditAccount } from "context/action";

/**
 * Lets the learner change language, dark mode, and notification preferences.
 */
const SettingsPreferences = () => {
  const { i18n, t } = useTranslation("common");

  const [controller, dispatch] = useAsterController();
  const { darkMode, user, isProfileUpdateLoading } = controller;

  const [language, setLanguage] = useState("fr");

  useEffect(() => {
    const lang = localStorage.getItem("language") || "fr";
    i18n.changeLanguage(lang);
    setLanguage(lang);

    const mode = localStorage.getItem("dark") === "true";
    if (mode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    actionDarkMode(dispatch, mode);
  }, [i18n, dispatch]);

  /**
   * Toggles dark mode and persists it in localStorage.
   *
   * @returns {void}
   */
  const onDarkModeClick = () => {
    if (darkMode) {
      document.documentElement.classList.remove("dark");
    } else {
      document.documentElement.classList.add("dark");
    }
    actionDarkMode(dispatch, !darkMode);
    localStorage.setItem("dark", !darkMode);
  };

  /**
   * Updates i18n language and persists the choice.
   *
   * @param {React.ChangeEvent} e Select change event.
   * @returns {void}
   */
  const onLanguageChange = (e) => {
    setLanguage(e.target.value);
    i18n.changeLanguage(e.target.value);
    localStorage.setItem("language", e.target.value);
  };

  /**
   * Saves preference fields to the learner account.
   *
   * @param {Object} values Form values.
   * @returns {void}
   */
  const onSubmit = (values) => {
    const data = {
      id: user.id,
      email: user.email,
      ...values,
    };

    if (!values.old_password) {
      delete data.old_password;
      delete data.new_password;
      delete data.confirm_password;
    }

    actionEditAccount(dispatch, data, t);
  };

  const formik = useFormik({
    initialValues: {
      // email_announce: Boolean(user.email_announce) || false,
      email_reminder: Boolean(user.email_reminder) || false,
      email_comment: Boolean(user.email_comment) || false,
      // sms_announce: Boolean(user.sms_announce) || false,
      // sms_reminder: Boolean(user.sms_reminder) || false,
      // sms_comment: Boolean(user.sms_comment) || false,
      // phone_announce: Boolean(user.phone_announce) || false,
      // phone_resume: Boolean(user.phone_resume) || false,

      old_password: "",
      new_password: "",
      confirm_password: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      new_password: Yup.string().when("old_password", {
        is: (val) => !!val,
        then: Yup.string().min(8, t("Password must be at least 8 characters")).required(t("Required")),
        otherwise: Yup.string(),
      }),
      confirm_password: Yup.string().when("old_password", {
        is: (val) => !!val,
        then: Yup.string().oneOf([Yup.ref("new_password")], t("Passwords must match")).required(t("Required")),
        otherwise: Yup.string(),
      }),
    }),
    onSubmit: onSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box className="flex md:flex-row flex-col md:items-center items-start justify-between">
        <VText className="text-2xl font-bold">{t("Your Preference")}</VText>

        <VButton variant="contained" color="primary" className="text-sm px-10 normal-case mt-3 md:mt-0" type="submit" loading={!isProfileUpdateLoading}>
          {t("Save")}
        </VButton>
      </Box>
      <Box className="mt-8">
        <VText className="text-xl">{t("Email notifications")}</VText>
        <Box className="mt-8 mb-12">
          <VText className="text-sm">{t("Check the types of emails you want to receive")}</VText>
        </Box>
        {/* <Box className="mt-4 ml-10 flex justify-between">
          <VText className="text-md">{t("Announcements related to training courses I have registered for")}</VText>
          <VSwitch type="Android" checked={formik.values.email_announce} setChecked={(val) => formik.setFieldValue("email_announce", val)} color="primary" className="ml-auto" />
        </Box> */}
        <Box className="mt-4 ml-10 flex justify-between">
          <VText className="text-md">{t("Reminders related to training courses I have registered for")}</VText>
          <VSwitch type="Android" checked={formik.values.email_reminder} setChecked={(val) => formik.setFieldValue("email_reminder", val)} color="primary" className="ml-auto" />
        </Box>
        <Box className="mt-4 ml-10 flex justify-between">
          <VText className="text-md">{t("Responses to my comments")}</VText>
          <VSwitch type="Android" checked={formik.values.email_comment} setChecked={(val) => formik.setFieldValue("email_comment", val)} color="primary" className="ml-auto" />
        </Box>
      </Box>

      {/* <Box className="mt-8">
        <VText className="text-xl">{t("SMS notifications")}</VText>
        <Box className="mt-8 mb-12">
          <VText className="text-sm">{t("Check the types of emails you want to receive")}</VText>
        </Box>
        <Box className="mt-4 ml-10 flex justify-between">
          <VText className="text-md">{t("Announcements related to training courses I have registered for")}</VText>
          <VSwitch type="Android" checked={formik.values.sms_announce} setChecked={(val) => formik.setFieldValue("sms_announce", val)} color="primary" className="ml-auto" />
        </Box>
        <Box className="mt-4 ml-10 flex justify-between">
          <VText className="text-md">{t("Reminders related to training courses I have registered for")}</VText>
          <VSwitch type="Android" checked={formik.values.sms_reminder} setChecked={(val) => formik.setFieldValue("sms_reminder", val)} color="primary" className="ml-auto" />
        </Box>
        <Box className="mt-4 ml-10 flex justify-between">
          <VText className="text-md">{t("Responses to my comments")}</VText>
          <VSwitch type="Android" checked={formik.values.sms_comment} setChecked={(val) => formik.setFieldValue("sms_comment", val)} color="primary" className="ml-auto" />
        </Box>
      </Box>

      <Box className="mt-8">
        <VText className="text-xl">{t("Phone")}</VText>
        <Box className="mt-11 ml-10 flex justify-between">
          <VText className="text-md">{t("Announcements related to training courses I have registered for")}</VText>
          <VSwitch type="Android" checked={formik.values.phone_announce} setChecked={(val) => formik.setFieldValue("phone_announce", val)} color="primary" className="ml-auto" />
        </Box>
        <Box className="mt-4 ml-10 flex justify-between">
          <VText className="text-md">{t("A quick phone call to help resume your training if you're running late?")}</VText>
          <VSwitch type="Android" checked={formik.values.phone_resume} setChecked={(val) => formik.setFieldValue("phone_resume", val)} color="primary" className="ml-auto" />
        </Box>
      </Box> */}

      <Box className="mt-4">
        <VText className="text-xl">{t("Language")}</VText>
        <Box className="mt-4">
          <VText className="text-sm">{t("Select the language you want to use in the platform:")}</VText>
        </Box>

        <RadioGroup row name="gender" value={language} onChange={onLanguageChange} className="mt-8 mx-10 flex justify-center md:gap-12 gap-6 md:justify-start">
          <FormControlLabel value="fr" control={<Radio color="primary" className="p-0 mr-2" />} label={<VText>Français</VText>} />
          <FormControlLabel value="uk" control={<Radio color="primary" className="p-0 mr-2" />} label={<VText>English</VText>} />
        </RadioGroup>
      </Box>

      <Box className="mt-8">
        <VText className="text-xl">{t("Light / Dark")}</VText>

        <RadioGroup row name="gender" value={darkMode} onChange={onDarkModeClick} className="mt-8 mx-10 flex justify-center md:gap-12 gap-6 md:justify-start">
          <FormControlLabel value={false} control={<Radio color="primary" className="p-0 mr-2" />} label={<VText>{t("Light")}</VText>} />
          <FormControlLabel value={true} control={<Radio color="primary" className="p-0 mr-2" />} label={<VText>{t("Dark")}</VText>} className="ml-2" />
        </RadioGroup>
      </Box>

      <Box className="mt-12">
        <VText className="text-2xl font-bold">{t("Account Settings")}</VText>
        <Box className="mt-8">
          <VText className="text-xl">{t("Reset Your Password")}</VText>

          <Box className="mt-5">
            <VFormItem formik={formik} label={t("Old Password")} name="old_password" required>
              <VPassword name="old_password" value={formik.values.old_password} onChange={formik.handleChange} className="w-full" inputClassName="py-3" placeholder="******" />
            </VFormItem>
          </Box>

          <Box className="mt-5">
            <VFormItem formik={formik} label={t("New Password")} name="new_password" required>
              <VPassword name="new_password" value={formik.values.new_password} onChange={formik.handleChange} className="w-full" inputClassName="py-3" placeholder="******" />
            </VFormItem>
          </Box>

          <Box className="mt-5">
            <VFormItem formik={formik} label={t("Confirm Password")} name="confirm_password" required>
              <VPassword name="confirm_password" value={formik.values.confirm_password} onChange={formik.handleChange} className="w-full" inputClassName="py-3" placeholder="******" />
            </VFormItem>
          </Box>
        </Box>
      </Box>

      <Box className="mt-8 border-2 border-solid border-gray-200 dark:border-gray-500" />

      <Box className="mt-8">
        <VText className="text-2xl font-bold color-primary" color="custom">
          {t("Danger Zone")}
        </VText>
        <Box className="mt-4">
          <VText color="secondary" className="text-xl">
            {t("Want to Delete your account? Do not hestitate to")}{" "}
            <a
              href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}?subject=${encodeURIComponent("Contact request")}&body=${encodeURIComponent(
                `Hello,\n\nI need help with...\n\nUser: ${user?.email || ""}\n`
              )}`}
              className="underline-offset-1 text-orange-600"
            >
              {t("Contact us")}
            </a>
          </VText>
        </Box>
      </Box>
    </form>
  );
};

export default SettingsPreferences;
